import React, { memo } from 'react';
import { CheckIcon, ChevronRightIcon } from 'lucide-react';
import { trackProductClick } from './MetaPixel';
export const ProductSection = () => {
  return <section className="mb-10" data-id="element-62">
      <div className="bg-white p-6 border border-gray-200 rounded-lg mb-8" data-id="element-63">
        <h2 className="text-3xl font-bold text-blue-800 mb-4" data-id="element-64">
          The simple mechanical solution that's been hiding in plain sight
        </h2>
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-6 italic" data-id="element-65">
          <p className="text-gray-700 text-lg" data-id="element-66">
            "Here's what most people don't understand," Dr. Mitchell continued.
            "Your pain isn't a medical mystery that requires complex treatments
            or dangerous surgeries. It's a mechanical problem that requires a
            mechanical solution."
          </p>
        </div>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-67">
          The solution isn't more medication. It isn't invasive procedures. It
          isn't even expensive physical therapy.
        </p>
        <p className="mb-4 text-gray-700 font-bold text-2xl text-center" data-id="element-68">
          It's proper spinal alignment during sleep.
        </p>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-69">
          Think about it: If misalignment is the root cause of your pain, then
          maintaining proper alignment should eliminate the cause entirely.
        </p>
        <p className="mb-6 text-gray-700 text-lg" data-id="element-70">
          But here's the challenge – how do you maintain perfect spinal
          alignment for 8 hours while you're unconscious and constantly moving?
        </p>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-71">
          Traditional pillows compress and shift. Stacking multiple pillows
          creates instability. Those generic "knee pillows" are designed with a
          one-size-fits-all approach that doesn't account for the precise angles
          needed for optimal alignment.
        </p>
        <p className="mb-4 text-center font-bold text-2xl text-blue-800" data-id="element-72">
          That's when I discovered the breakthrough that changed everything...
        </p>
      </div>
      <div className="bg-blue-700 text-white p-6 rounded-lg mb-8" data-id="element-73">
        <div className="flex flex-col md:flex-row gap-6 items-center" data-id="element-74">
          <div className="md:w-1/2" data-id="element-75">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/tQwuRnRop1cxELtSzP3AfY/81nMZ2BQvTqL._AC_SL1500_.png" alt="Relievana™ Alignment Pillow" className="rounded-lg w-full h-auto object-contain" data-id="element-76" />
          </div>
          <div className="md:w-1/2" data-id="element-77">
            <h3 className="text-2xl font-bold mb-4" data-id="element-78">Relievana™ Pillow</h3>
            <div className="mb-4" data-id="element-79">
              <div className="flex items-start mb-2" data-id="element-80">
                <CheckIcon className="mr-2 text-green-300 flex-shrink-0 mt-1" size={22} data-id="element-81" />
                <p className="text-lg" data-id="element-82">
                  Patented Precision Alignment Engineering™
                </p>
              </div>
              <div className="flex items-start mb-2" data-id="element-83">
                <CheckIcon className="mr-2 text-green-300 flex-shrink-0 mt-1" size={22} data-id="element-84" />
                <p className="text-lg" data-id="element-85">
                  Stops the Nocturnal Misalignment Cascade
                </p>
              </div>
              <div className="flex items-start mb-2" data-id="element-86">
                <CheckIcon className="mr-2 text-green-300 flex-shrink-0 mt-1" size={22} data-id="element-87" />
                <p className="text-lg" data-id="element-88">Advanced pressure-relief technology</p>
              </div>
              <div className="flex items-start mb-2" data-id="element-89">
                <CheckIcon className="mr-2 text-green-300 flex-shrink-0 mt-1" size={22} data-id="element-90" />
                <p className="text-lg" data-id="element-91">
                  Temperature-regulating CoolAlign™ memory foam
                </p>
              </div>
              <div className="flex items-start mb-4" data-id="element-92">
                <CheckIcon className="mr-2 text-green-300 flex-shrink-0 mt-1" size={22} data-id="element-93" />
                <p className="text-lg" data-id="element-94">
                  Anti-slip stability system for all-night alignment
                </p>
              </div>
            </div>
            <a href="https://relievana.com/products/relievana%E2%84%A2-alignment-pillow" className="bg-green-500 hover:bg-green-600 text-white py-3 px-6 rounded-lg font-bold flex items-center justify-center w-full transition-colors text-lg" onClick={trackProductClick} data-id="element-95">
              CHECK AVAILABILITY
              <ChevronRightIcon className="ml-2" size={22} data-id="element-96" />
            </a>
          </div>
        </div>
      </div>
      <div className="bg-white p-6 border border-gray-200 rounded-lg" data-id="element-97">
        <p className="mb-4 text-gray-700 text-lg" data-id="element-98">
          A team of biomechanical engineers and sleep specialists spent over
          three years developing what they call "Precision Alignment
          Engineering™" – a revolutionary approach that goes far beyond
          traditional pillow design.
        </p>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-99">
          The Relievana™ Pillow features a patented, data-driven ergonomic
          shape that was specifically engineered to match the natural curves and
          angles of the human body during side sleeping.
        </p>
        <p className="mb-4 text-gray-700 font-bold text-lg" data-id="element-100">
          But here's what makes it truly different:
        </p>
        <p className="mb-4 text-gray-700 text-2xl text-center font-bold text-blue-800" data-id="element-101">
          This isn't just a pillow – it's a precision tool.
        </p>
        <div className="flex flex-col md:flex-row gap-6 mb-6" data-id="element-102">
          <div className="md:w-1/2" data-id="element-103">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jNzfT6TnHa8dDSmyn9hviu/thighwbenefits.jpg" alt="Relievana™ Pillow benefits for different conditions" className="rounded-lg w-full h-auto object-contain" data-id="element-104" />
          </div>
          <div className="md:w-1/2" data-id="element-105">
            <p className="mb-4 text-gray-700 text-lg" data-id="element-106">
              The contoured design cradles your leg at the exact 30-degree angle
              needed to keep your spine in perfect neutral alignment. The
              advanced CoolAlign™ memory foam provides ideal support without
              "bottoming out" like cheaper alternatives, while the anti-slip
              base ensures it stays in position even if you're a restless
              sleeper.
            </p>
            <p className="mb-4 text-gray-700 font-bold text-lg" data-id="element-107">
              Most importantly, it stops the Nocturnal Misalignment Cascade at
              its source.
            </p>
            <p className="text-gray-700 text-lg" data-id="element-108">
              With your spine properly aligned, pressure is instantly relieved
              from your sciatic nerve. Your hip joint can rest without
              compression. Your body can finally do what it's designed to do
              during sleep – heal and recover instead of fighting misalignment
              all night long.
            </p>
          </div>
        </div>
        <h3 className="text-2xl font-bold text-blue-800 mb-4" data-id="element-109">
          "This pillow gave me my life back"
        </h3>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-110">
          Within the first week of using Relievana™, something remarkable
          happened.
        </p>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-111">
          For the first time in three years, I slept through the entire night
          without waking up in pain. But the real miracle came the next morning
          when I stood up from bed and realized something was missing – that
          familiar sharp shooting pain that had become my unwelcome morning
          companion.
        </p>
        <p className="mb-4 text-gray-700 font-bold text-lg" data-id="element-112">
          I actually stood up straight without thinking about it.
        </p>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-113">
          By the second week, the deep hip ache that made simple tasks feel
          impossible had virtually disappeared. I found myself doing things I'd
          forgotten I could do – like putting on socks without wincing, or
          walking to the mailbox without planning my route around potential rest
          stops.
        </p>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-114">
          But the transformation went deeper than just physical relief.
        </p>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-115">
          My wife told me she could see "the old James" coming back. My energy
          returned. My mood improved. I started participating in family
          activities again instead of watching from the sidelines with a forced
          smile while secretly battling pain.
        </p>
        <p className="mb-6 text-gray-700 text-lg" data-id="element-116">
          I had reclaimed my identity. I wasn't a "sciatica sufferer" anymore –
          I was a husband, father, and active person who happened to sleep with
          a precision-engineered pillow.
        </p>
        <h3 className="text-2xl font-bold text-blue-800 mb-4" data-id="element-117">
          Here's exactly how to end your pillow saga tonight
        </h3>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-118">
          The beauty of Relievana™ is its elegant simplicity:
        </p>
        <div className="mb-6" data-id="element-119">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-3" data-id="element-120">
            <p className="font-bold text-blue-800 mb-1 text-lg" data-id="element-121">
              STEP 1: POSITION FOR ALIGNMENT
            </p>
            <p className="text-gray-700 text-lg" data-id="element-122">
              Simply place the pillow between your knees and thighs when you lie
              on your side. The patented contour naturally guides your leg into
              the optimal position.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-3" data-id="element-123">
            <p className="font-bold text-blue-800 mb-1 text-lg" data-id="element-124">
              STEP 2: LET PRECISION ENGINEERING WORK
            </p>
            <p className="text-gray-700 text-lg" data-id="element-125">
              The pillow's ergonomic design automatically maintains proper
              spinal alignment, even as you move during sleep. No adjustment
              needed.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-3" data-id="element-126">
            <p className="font-bold text-blue-800 mb-1 text-lg" data-id="element-127">
              STEP 3: WAKE UP PAIN-FREE
            </p>
            <p className="text-gray-700 text-lg" data-id="element-128">
              Your spine decompresses naturally throughout the night, breaking
              the cycle of misalignment and inflammation.
            </p>
          </div>
        </div>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-129">
          That's it. No complicated routines. No multiple pillows to arrange. No
          hoping it stays in place.
        </p>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-130">
          The pillow might feel slightly different the first night – that's
          actually a good sign. It means your body is experiencing proper
          alignment, possibly for the first time in years. Most people report
          feeling completely comfortable within 1-2 nights.
        </p>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-131">
          The advanced materials ensure comfort in every season: the cooling
          gel-infused memory foam prevents overheating, while the
          moisture-wicking bamboo cover keeps you dry and comfortable all night
          long.
        </p>
        <p className="mb-4 text-gray-700 text-lg" data-id="element-132">
          I was initially concerned about durability, but after eight months of
          nightly use, my Relievana™ pillow maintains its shape and support as
          if it were brand new. I'll never have to return to my pillow saga.
        </p>
      </div>
    </section>;
};